/**
 * F1 Alquiler (oct-2026) — campos compartidos de términos de alquiler (monto, frecuencia, día de cobro,
 * condiciones) + selector Comisión | Alquiler + helpers de formato. Usado por Invitar, Proponer acuerdo
 * y las vistas del barbero.
 */
import { Percent, KeyRound } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { tr, localeTag } from '@/contexts/LanguageContext';

export const WEEKDAYS = () => [tr("lunes"), tr("martes"), tr("miércoles"), tr("jueves"), tr("viernes"), tr("sábado"), tr("domingo")];
export const EMPTY_RENT = { amount: '', frequency: 'weekly', due_day: '0', notes: '' };

export const fmtRentMoney = (v) => `$${Number(v || 0).toLocaleString(localeTag(), { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/** «$150 semanal · cobro los lunes» / «$600 mensual · cobro el día 5». */
export const describeRent = (rent) => {
  if (!rent || !rent.amount || !rent.frequency) return tr("alquiler por definir");
  const base = rent.frequency === 'weekly' ? tr("{0} semanal", fmtRentMoney(rent.amount)) : tr("{0} mensual", fmtRentMoney(rent.amount));
  if (rent.due_day === null || rent.due_day === undefined) return base;
  return rent.frequency === 'weekly'
    ? tr("{0} · cobro los {1}", base, WEEKDAYS()[Number(rent.due_day)] || '')
    : tr("{0} · cobro el día {1}", base, rent.due_day);
};

export const rentToForm = (rent) => ({
  amount: rent?.amount != null ? String(rent.amount) : '',
  frequency: rent?.frequency || 'weekly',
  due_day: rent?.due_day != null ? String(rent.due_day) : (rent?.frequency === 'monthly' ? '1' : '0'),
  notes: rent?.notes || '',
});

export const formToRent = (f) => ({
  amount: f.amount === '' ? null : Number(f.amount),
  frequency: f.frequency,
  due_day: f.due_day === '' ? null : Number(f.due_day),
  notes: f.notes?.trim() || null,
});

export const validateRentForm = (f) => {
  const n = Number(f.amount);
  if (!Number.isFinite(n) || n < 1) return tr("Indica el monto del alquiler");
  if (!['weekly', 'monthly'].includes(f.frequency)) return tr("Elige la frecuencia del alquiler");
  const d = Number(f.due_day);
  if (f.frequency === 'weekly' && !(d >= 0 && d <= 6)) return tr("Elige el día de cobro");
  if (f.frequency === 'monthly' && !(d >= 1 && d <= 28)) return tr("El día de cobro mensual va del 1 al 28");
  return null;
};

export const ModeToggle = ({ value, onChange, testPrefix = 'shop-mode' }) => (
  <div className="grid grid-cols-2 gap-2" data-testid={`${testPrefix}-toggle`}>
    {[
      { v: 'pct', icon: Percent, title: tr("Por comisión"), desc: tr("La barbería recibe un % de cada cita") },
      { v: 'rent', icon: KeyRound, title: tr("Por alquiler"), desc: tr("Renta fija por el puesto · el barbero se queda con el 100 % (menos Stripe)") },
    ].map(({ v, icon: Icon, title, desc }) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className={`text-left rounded-xl border p-3 transition-colors ${value === v ? 'border-primary bg-primary/10' : 'border-border bg-background hover:border-primary/40'}`}
        aria-pressed={value === v} data-testid={`${testPrefix}-${v}`}>
        <div className="flex items-center gap-1.5 mb-0.5"><Icon className={`w-3.5 h-3.5 ${value === v ? 'text-primary' : 'text-muted-foreground'}`} /><span className="text-xs font-semibold text-foreground">{title}</span></div>
        <p className="text-[10px] text-muted-foreground leading-snug">{desc}</p>
      </button>
    ))}
  </div>
);

export const RentTermsFields = ({ value, onChange, testPrefix = 'shop-rent' }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e?.target ? e.target.value : e });
  const setFreq = (freq) => onChange({ ...value, frequency: freq, due_day: freq === 'weekly' ? '0' : '1' });
  const days = WEEKDAYS();
  return (
    <div className="space-y-3" data-testid={`${testPrefix}-fields`}>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label htmlFor={`${testPrefix}-amount`}>{tr("Monto")}</Label>
          <Input id={`${testPrefix}-amount`} type="number" inputMode="decimal" min="1" step="1" value={value.amount} onChange={set('amount')}
            placeholder="150" className="bg-background border-border" data-testid={`${testPrefix}-amount`} />
        </div>
        <div className="space-y-1">
          <Label>{tr("Frecuencia")}</Label>
          <div className="grid grid-cols-2 rounded-lg border border-border overflow-hidden" data-testid={`${testPrefix}-frequency`}>
            {[['weekly', tr("Semanal")], ['monthly', tr("Mensual")]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setFreq(v)}
                className={`h-9 text-xs font-semibold ${value.frequency === v ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground'}`}
                data-testid={`${testPrefix}-frequency-${v}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${testPrefix}-due`}>{tr("Día de cobro")}</Label>
        <select id={`${testPrefix}-due`} value={value.due_day} onChange={set('due_day')}
          className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground" data-testid={`${testPrefix}-due-day`}>
          {value.frequency === 'weekly'
            ? days.map((d, i) => <option key={i} value={String(i)}>{d}</option>)
            : Array.from({ length: 28 }, (_, i) => <option key={i + 1} value={String(i + 1)}>{tr("día {0}", i + 1)}</option>)}
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${testPrefix}-notes`}>{tr("Condiciones / notas")}</Label>
        <Textarea id={`${testPrefix}-notes`} value={value.notes} onChange={set('notes')} maxLength={500} rows={2}
          placeholder={tr("Ej. incluye sillón 2, agua y luz; se paga en efectivo o Zelle")} className="bg-background border-border text-sm" data-testid={`${testPrefix}-notes`} />
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug">{tr("El alquiler se cobra por fuera de la app y se registra a mano en tu panel. El barbero no paga comisión por cita: cada cobro va 100 % a su cuenta (menos la tarifa de Stripe).")}</p>
    </div>
  );
};
